import React from 'react';
import { Link } from 'react-router-dom';
import { Tag } from 'primereact/tag';
import { convertSecondsToTime, dollarFormat, getPropByString } from 'utils/utils';
import { classNames } from 'primereact/utils';
import { handleSettingSkAndPkToLocalStorage } from 'utils/localStorageUtils';
import moment from 'moment';
import { Button } from 'primereact/button';
import { Tooltip } from 'primereact/tooltip';
import { generateFullName } from 'utils/generateFullNameUtils';

const defaultClassName = 'me-2 text-decoration-none';
const emptyDataValue = '-';

export const tableCell = (row, ...rest) => {
  const { header, field } = rest?.[0];
  return (
    <React.Fragment>
      <span className="p-column-title text-break">{header}</span>
      <span className="text-break" title={getPropByString(row, field)}>
        {getPropByString(row, field) || '-'}
      </span>
    </React.Fragment>
  );
};

export const pastDueAmountTableCell = (row, ...rest) => {
  const { header, field } = rest?.[0];
  const amount =
    row?.plan_current_amount && row?.plan_current_status === 'PARTIAL' ? row?.plan_current_amount : getPropByString(row, field);

  return (
    <React.Fragment>
      <span className="p-column-title text-break">{header}</span>
      <span className="text-break" title={amount ? amount : undefined}>
        {amount ? amount : '-'}
      </span>
    </React.Fragment>
  );
};

export const currencyCell = (row, ...rest) => {
  const { header, field } = rest?.[0];
  return (
    <React.Fragment>
      <span className="p-column-title text-break">{header}</span>
      <span className="text-break" title={dollarFormat(getPropByString(row, field))}>
        {dollarFormat(getPropByString(row, field))}
      </span>
    </React.Fragment>
  );
};

export const waitingPaymentCurrencyCell = (row, ...rest) => {
  const { header, field } = rest?.[0];

  const amount = row?.is_payment_plan && row?.plan_due_amount ? row?.plan_due_amount : getPropByString(row, field);

  return (
    <React.Fragment>
      <span className="p-column-title text-break">{header}</span>
      <span className="text-break" title={amount ? dollarFormat(amount) : undefined}>
        {amount ? dollarFormat(amount) : '-'}
      </span>
    </React.Fragment>
  );
};

export const numberCell = (row, ...rest) => {
  const { header, field } = rest?.[0];
  return (
    <React.Fragment>
      <span className="p-column-title text-break">{header}</span>
      <span className="text-break" title={getPropByString(row, field) ? Number(getPropByString(row, field)) : undefined}>
        {getPropByString(row, field) ? Number(getPropByString(row, field)) : '-'}
      </span>
    </React.Fragment>
  );
};

export const nameTemplate = (rowData, ...rest) => {
  const { header, field } = rest?.[0];

  const { is_lead, case_id, case_description, invoice } = rowData || {};
  const isLead = is_lead;

  const mainModule = isLead ? '/leads' : '/cases';
  const caseId = case_id || invoice?.case_id;
  const route = caseId ? `${mainModule}/${caseId}` : mainModule;

  const isRedirectionLink = Boolean(case_description?.case_name || rowData?.case_name);
  const value = getPropByString(rowData, field);
  const stringDataValue = typeof value === 'object' || value === undefined || value === null ? '-' : value;

  return (
    <React.Fragment>
      <span className="p-column-title text-break">{header}</span>
      <span className="text-break">
        {isRedirectionLink ? (
          <Link
            to={route}
            className={`me-2 ${classNames({ 'redirection-link-no-text-transform': isRedirectionLink })}`}
            title={stringDataValue}
          >
            {stringDataValue}
          </Link>
        ) : (
          emptyDataValue
        )}
      </span>
    </React.Fragment>
  );
};

export const ContactNameTemplate = (rowData, ...rest) => {
  const { header } = rest?.[0];
  const { client_details } = rowData || {};
  let contactName;
  if (client_details) {
    const firstName = client_details?.first_name || '';
    const lastName = client_details?.last_name || '';
    contactName = `${firstName} ${lastName}`.trim() || null;
  }
  const isRedirectionLink = Boolean(contactName);
  const route = client_details?.contact_id ? '/contacts/' + client_details?.contact_id : '/contacts';

  return (
    <React.Fragment>
      <span className="p-column-title text-break">{header}</span>
      <span className="text-break">
        {isRedirectionLink ? (
          <Link
            to={route}
            className={`${defaultClassName} ${classNames({ 'redirection-link-no-text-transform': isRedirectionLink })}`}
            title={contactName || undefined}
          >
            {contactName || '-'}
          </Link>
        ) : (
          emptyDataValue
        )}
      </span>
    </React.Fragment>
  );
};

export const leadAttorneyTemplate = (rowData, ...rest) => {
  const { header } = rest?.[0];
  const { leadAttorney } = rowData || {};

  // Use pre-built attorney_name from the data mapping
  const attorneyName = leadAttorney?.attorney_name || generateFullName(leadAttorney) || '';

  const hasValidAttorney = attorneyName && attorneyName !== 'N/A' && attorneyName !== '-' && attorneyName.trim() !== '';
  const isRedirectionLink = Boolean(hasValidAttorney && leadAttorney?.firm_user_id);
  const route = leadAttorney?.firm_user_id ? '/settings/team-management/' + leadAttorney?.firm_user_id : '/settings/team-management';

  const handleClick = () => {
    if (leadAttorney?.sk && leadAttorney?.pk) {
      handleSettingSkAndPkToLocalStorage(leadAttorney?.sk, leadAttorney?.pk);
    }
  };

  return (
    <React.Fragment>
      <span className="p-column-title text-break">{header}</span>
      <span className="text-break">
        {isRedirectionLink ? (
          <Link
            to={route}
            onClick={handleClick}
            className={`${defaultClassName} ${classNames({ 'redirection-link-no-text-transform': isRedirectionLink })}`}
            title={attorneyName || undefined}
          >
            {leadAttorney?.firm_user_id && <i className="fas fa-user-tie me-2"></i>}
            {attorneyName}
          </Link>
        ) : (
          <span title={attorneyName || undefined}>{attorneyName || emptyDataValue}</span>
        )}
      </span>
    </React.Fragment>
  );
};

export const firmUserCell = (rowData, ...rest) => {
  const { header, field } = rest?.[0];
  const firmUserName = generateFullName(rowData) ? generateFullName(rowData) : rowData?.[field];

  return (
    <React.Fragment>
      <span className="p-column-title text-break">{header}</span>
      <span className="text-break">
        {rowData?.firm_user_id && <i className="fas fa-user-tie me-2"></i>}
        <span title={firmUserName}>{firmUserName || emptyDataValue}</span>
      </span>
    </React.Fragment>
  );
};

const normalizeTags = (tagNames) => {
  if (tagNames == null || tagNames === '') return [];
  if (Array.isArray(tagNames)) {
    return tagNames
      .map((v) => {
        if (typeof v === 'string') return v.trim();
        if (v != null && typeof v === 'object') {
          const label = v.name ?? v.label;
          return label != null ? String(label).trim() : '';
        }
        return String(v).trim();
      })
      .filter(Boolean);
  }
  return String(tagNames)
    .split(', ')
    .map((t) => t.trim())
    .filter(Boolean);
};

export const tagTemplate = (rowData) => {
  const sanitizedId = rowData?.case_id;
  const tagArray = normalizeTags(rowData?.case_details?.tags);
  const displayedTags = tagArray.slice(0, 1);
  const remainingTags = tagArray.slice(1);

  return (
    <>
      <span className="p-column-title text-break">Tags</span>

      {tagArray.length === 0 ? (
        <span className="text-break">{emptyDataValue}</span>
      ) : (
        <>
          {displayedTags.map((v, i) => (
            <span className="text-break" title={v} key={i}>
              <Tag value={v} className="d-tag me-1 mb-1 tag-ellipsis-text" />
            </span>
          ))}
          {remainingTags.length > 0 && (
            <>
              <Tag className={`p-mr-2 tag more-tag more-tag-${sanitizedId}`} rounded value={`+${remainingTags.length} more`} />
              <Tooltip target={`.more-tag-${sanitizedId}`} position="top">
                {remainingTags.map((tag, index) => (
                  <Tag key={index} value={tag} className="d-tag me-1 mb-1" />
                ))}
              </Tooltip>
            </>
          )}
        </>
      )}
    </>
  );
};

export const timeTemplate = (row, ...rest) => {
  const { field } = rest?.[0];
  return (
    <React.Fragment>
      <span className="p-column-title text-break">Total Time Open</span>
      <span className="text-break" title={convertSecondsToTime(getPropByString(row, field)) || undefined}>
        {convertSecondsToTime(getPropByString(row, field)) || '-'}
      </span>
    </React.Fragment>
  );
};

export const caseStageTemplate = (rowData, ...rest) => {
  const { header } = rest?.[0];

  const caseStage = rowData?.case_description?.case_stage?.stage_name;

  return (
    <React.Fragment>
      <span className="p-column-title text-break">{header}</span>
      <span className="text-break" title={caseStage || undefined}>
        {caseStage || '-'}
      </span>
    </React.Fragment>
  );
};
export const caseMatterTemplate = (rowData, ...rest) => {
  const { header } = rest?.[0];

  const caseMatter = rowData?.case_description.case_matter;

  return (
    <React.Fragment>
      <span className="p-column-title text-break">{header}</span>
      <span className="text-break" title={caseMatter || undefined}>
        {caseMatter || '-'}
      </span>
    </React.Fragment>
  );
};

export const timePastDueTemplate = (rowData, ...rest) => {
  const { header } = rest?.[0];
  const seconds = moment().diff(moment(rowData?.due_date), 'seconds');
  return (
    <React.Fragment>
      <span className="p-column-title text-break">{header}</span>
      <span className="text-break" title={convertSecondsToTime(seconds)}>
        {convertSecondsToTime(seconds)}
      </span>
    </React.Fragment>
  );
};

export const waitingPaymentsTimePastDueTemplate = (rowData, ...rest) => {
  const { header } = rest?.[0];

  const targetDate = rowData?.is_payment_plan && rowData?.last_unpaid_due_date ? rowData?.last_unpaid_due_date : rowData?.due_date;

  const seconds = targetDate ? moment().diff(moment(targetDate), 'seconds') : null;

  return (
    <React.Fragment>
      <span className="p-column-title text-break">{header}</span>
      <span className="text-break" title={seconds !== null ? convertSecondsToTime(seconds) : undefined}>
        {seconds !== null ? convertSecondsToTime(seconds) : '-'}
      </span>
    </React.Fragment>
  );
};

export const attorneyTemplate = (rowData, setRemainingattorneysData, overlayPanelRef) => {
  const sanitizedPk = rowData?.pk.replace(/[^a-zA-Z0-9_-]/g, '_');
  const attorneys = rowData?.attorney_details?.filter((v) => !v?.is_lead_attorney);
  const displayedAttorney = attorneys?.slice(0, 1);
  const remainingAttorneys = attorneys?.slice(1);

  const handleMoreClick = (e) => {
    setRemainingattorneysData(remainingAttorneys);
    overlayPanelRef?.current?.toggle(e);
  };
  return (
    <React.Fragment>
      <span className="p-column-title text-break">Attorney</span>
      <span className="text-break">
        {displayedAttorney?.length > 0
          ? displayedAttorney.map((attorney, i) => {
              const attorneyFullName =
                attorney?.first_name || attorney?.last_name ? `${attorney?.first_name || ''} ${attorney?.last_name || ''}`.trim() : null;
              const isRedirectionLink = Boolean(attorney?.first_name);
              const route = attorney?.firm_user_id ? '/settings/team-management/' + attorney?.firm_user_id : '/settings/team-management';
              if (attorney?.firm_user_id && isRedirectionLink && attorney?.sk && attorney?.pk) {
                handleSettingSkAndPkToLocalStorage(attorney?.sk, attorney?.pk);
              }

              return isRedirectionLink ? (
                <Link
                  key={i}
                  to={route}
                  className={`me-2 text-decoration-none ${classNames({ 'redirection-link-no-text-transform': isRedirectionLink })}`}
                  title={attorneyFullName || undefined}
                >
                  {attorney?.firm_user_id && <i className="fas fa-user-tie me-2"></i>}
                  {attorneyFullName || emptyDataValue}
                  <br />
                </Link>
              ) : (
                emptyDataValue
              );
            })
          : emptyDataValue}
        {remainingAttorneys?.length > 0 && (
          <div className="position-relative">
            <Button
              className={`p-mr-2 pa-tags more-tag-button more-tag-${sanitizedPk}`}
              label={`+${remainingAttorneys.length} more`}
              onClick={handleMoreClick}
              style={{ padding: '5px 10px' }}
            />
          </div>
        )}
      </span>
    </React.Fragment>
  );
};

export const leadCaseManagerTemplate = (rowData) => {
  const leadCaseMangerList = rowData?.attorney_details?.filter((v) => v?.is_lead_case_manager);
  return (
    <React.Fragment>
      <span className="p-column-title text-break">Lead Case Manager</span>
      <span className="text-break">
        {leadCaseMangerList?.length > 0
          ? leadCaseMangerList.map((attorney, i) => {
              const isRedirectionLink = Boolean(generateFullName(attorney) ? generateFullName(attorney) : attorney?.attorney_name);
              const route = attorney?.firm_user_id ? '/settings/team-management/' + attorney?.firm_user_id : '/settings/team-management';
              if (attorney?.firm_user_id && isRedirectionLink && attorney?.sk && attorney?.pk) {
                handleSettingSkAndPkToLocalStorage(attorney?.sk, attorney?.pk);
              }

              return isRedirectionLink ? (
                <Link
                  key={i}
                  to={route}
                  className={`me-2 text-decoration-none ${classNames({ 'redirection-link-no-text-transform': isRedirectionLink })}`}
                  title={generateFullName(attorney) ? generateFullName(attorney) : attorney?.attorney_name || undefined}
                >
                  {attorney?.firm_user_id && <i className="fas fa-user-tie me-2"></i>}
                  {generateFullName(attorney) ? generateFullName(attorney) : attorney?.attorney_name || emptyDataValue}
                  <br />
                </Link>
              ) : (
                emptyDataValue
              );
            })
          : emptyDataValue}
      </span>
    </React.Fragment>
  );
};

// Cell renderer for Group Report RDS that shows 0 values instead of dashes
export const groupReportTableCell = (row, ...rest) => {
  const { header, field } = rest?.[0];
  const value = getPropByString(row, field);

  return (
    <React.Fragment>
      <span className="p-column-title text-break">{header}</span>
      <span className="text-break" title={value !== null && value !== undefined ? value : undefined}>
        {value !== null && value !== undefined ? value : '-'}
      </span>
    </React.Fragment>
  );
};
